import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Newspaper, RefreshCw, Eye, EyeOff, Pin, Trash2, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CAT_LABELS = { relationships: "연애와 사랑", travel: "여행", health: "건강", hobby: "취미", retirement: "노후 & 인생2막" };

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())}`;
};

export default function AdminCommunityNewsPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [settings, setSettings] = useState(null);
  const [articles, setArticles] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [collecting, setCollecting] = useState(false);
  const H = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const loadSettings = useCallback(async () => {
    const r = await fetch(`${API_URL}/api/admin/community-news/settings`, { headers: H });
    if (r.ok) setSettings(await r.json());
  }, [token]);

  const loadArticles = useCallback(async () => {
    setLoading(true);
    const url = `${API_URL}/api/admin/community-news${filter ? `?category=${filter}` : ""}`;
    const r = await fetch(url, { headers: H });
    if (r.ok) { const d = await r.json(); setArticles(d.articles || []); }
    setLoading(false);
  }, [token, filter]);

  useEffect(() => { loadSettings(); }, [loadSettings]);
  useEffect(() => { loadArticles(); }, [loadArticles]);

  const saveSettings = async (patch) => {
    const r = await fetch(`${API_URL}/api/admin/community-news/settings`, { method: "PUT", headers: H, body: JSON.stringify(patch) });
    if (r.ok) { setSettings(await r.json()); toast.success("설정을 저장했어요"); }
  };

  const runCollect = async () => {
    setCollecting(true);
    const r = await fetch(`${API_URL}/api/admin/community-news/collect`, { method: "POST", headers: H });
    const d = await r.json();
    toast(d.message || "수집을 시작했어요");
    setTimeout(() => { loadArticles(); setCollecting(false); }, 8000);
  };

  const patchArticle = async (id, body) => {
    const r = await fetch(`${API_URL}/api/admin/community-news/${id}`, { method: "PATCH", headers: H, body: JSON.stringify(body) });
    if (r.ok) loadArticles(); else toast.error("변경 실패");
  };
  const delArticle = async (id) => {
    if (!window.confirm("이 기사를 삭제할까요?")) return;
    await fetch(`${API_URL}/api/admin/community-news/${id}`, { method: "DELETE", headers: H });
    loadArticles();
  };

  const kwText = (obj) => Object.entries(obj || {}).map(([k, v]) => `${k}: ${(v || []).join(", ")}`).join("\n");

  return (
    <div className="min-h-screen bg-stone-50 pt-24 pb-20" data-testid="admin-community-news">
      <div className="max-w-5xl mx-auto px-4">
        <button onClick={() => navigate("/admin")} className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-700 mb-4"><ArrowLeft className="w-4 h-4" /> 관리자 홈</button>

        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Newspaper className="w-6 h-6 text-burgundy-600" strokeWidth={2.2} />
            <h1 className="text-2xl font-bold text-stone-900">카테고리 뉴스 관리</h1>
          </div>
          <div className="flex items-center gap-2">
            {settings && (
              <button
                onClick={() => saveSettings({ enabled: !settings.enabled })}
                className={`px-3.5 py-2 rounded-lg text-sm font-bold ${settings.enabled ? "bg-emerald-600 text-white" : "bg-stone-300 text-stone-700"}`}
                data-testid="cnews-toggle-enabled"
              >
                자동 뉴스 {settings.enabled ? "ON" : "OFF"}
              </button>
            )}
            <button onClick={runCollect} disabled={collecting} className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-burgundy-600 hover:bg-burgundy-700 text-white text-sm font-bold disabled:opacity-60" data-testid="cnews-collect-btn">
              <RefreshCw className={`w-4 h-4 ${collecting ? "animate-spin" : ""}`} /> 지금 수집
            </button>
          </div>
        </div>

        {/* Settings */}
        {settings && (
          <div className="bg-white rounded-2xl border border-stone-200 p-4 mb-5 space-y-3">
            <div className="grid sm:grid-cols-3 gap-3">
              <label className="text-sm">
                <span className="block text-stone-500 mb-1">최소 관련성 점수</span>
                <input type="number" defaultValue={settings.min_relevance} onBlur={(e) => saveSettings({ min_relevance: parseInt(e.target.value) || 62 })} className="w-full px-3 py-2 rounded-lg border border-stone-200" data-testid="cnews-min-rel" />
              </label>
              <label className="text-sm">
                <span className="block text-stone-500 mb-1">카테고리당 노출 수</span>
                <input type="number" defaultValue={settings.per_category} onBlur={(e) => saveSettings({ per_category: parseInt(e.target.value) || 1 })} className="w-full px-3 py-2 rounded-lg border border-stone-200" data-testid="cnews-per-cat" />
              </label>
              <label className="text-sm">
                <span className="block text-stone-500 mb-1">제외 언론사 (쉼표)</span>
                <input defaultValue={(settings.exclude_sources || []).join(", ")} onBlur={(e) => saveSettings({ exclude_sources: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="w-full px-3 py-2 rounded-lg border border-stone-200" data-testid="cnews-excl-src" />
              </label>
            </div>
            <label className="text-sm block">
              <span className="block text-stone-500 mb-1">제외 키워드 (쉼표)</span>
              <input defaultValue={(settings.exclude_keywords || []).join(", ")} onBlur={(e) => saveSettings({ exclude_keywords: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="w-full px-3 py-2 rounded-lg border border-stone-200" data-testid="cnews-excl-kw" />
            </label>
            <label className="text-sm block">
              <span className="block text-stone-500 mb-1">카테고리별 검색 키워드 (한 줄에 `카테고리키: 키워드1, 키워드2`)</span>
              <textarea
                rows={5}
                defaultValue={kwText(settings.keywords)}
                onBlur={(e) => {
                  const obj = {};
                  e.target.value.split("\n").forEach((line) => {
                    const idx = line.indexOf(":");
                    if (idx > 0) {
                      const key = line.slice(0, idx).trim();
                      if (CAT_LABELS[key]) obj[key] = line.slice(idx + 1).split(",").map((s) => s.trim()).filter(Boolean);
                    }
                  });
                  if (Object.keys(obj).length) saveSettings({ keywords: obj });
                }}
                className="w-full px-3 py-2 rounded-lg border border-stone-200 font-mono text-[12px]"
                data-testid="cnews-keywords"
              />
              <span className="text-[11px] text-stone-400">키: relationships, travel, health, hobby, retirement</span>
            </label>
          </div>
        )}

        {/* Filter */}
        <div className="flex flex-wrap gap-2 mb-3">
          <button onClick={() => setFilter("")} className={`px-3 py-1.5 rounded-full text-[13px] font-semibold ${!filter ? "bg-burgundy-600 text-white" : "bg-white border border-stone-200 text-stone-600"}`}>전체</button>
          {Object.entries(CAT_LABELS).map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-full text-[13px] font-semibold ${filter === k ? "bg-burgundy-600 text-white" : "bg-white border border-stone-200 text-stone-600"}`} data-testid={`cnews-filter-${k}`}>{l}</button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-burgundy-400" /></div>
        ) : articles.length === 0 ? (
          <p className="text-center text-stone-400 py-16">수집된 기사가 없습니다. '지금 수집'을 눌러보세요.</p>
        ) : (
          <div className="space-y-2" data-testid="cnews-list">
            {articles.map((a) => (
              <div key={a.id} className={`bg-white rounded-xl border p-3 ${a.hidden ? "border-stone-200 opacity-60" : "border-stone-200"}`} data-testid={`cnews-item-${a.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                      <span className="text-[10.5px] font-bold text-burgundy-600 bg-burgundy-50 px-1.5 py-0.5 rounded">{CAT_LABELS[a.community_category] || a.community_category}</span>
                      {a.is_pinned && <span className="text-[10.5px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">고정</span>}
                      {a.hidden && <span className="text-[10.5px] font-bold text-stone-500 bg-stone-100 px-1.5 py-0.5 rounded">숨김</span>}
                    </div>
                    <a href={a.url} target="_blank" rel="noopener noreferrer" className="text-[14px] font-semibold text-stone-800 hover:text-burgundy-700">{a.korean_title}</a>
                    <div className="text-[11.5px] text-stone-400 mt-0.5">{a.source_name} · <span className="font-semibold text-stone-500">{fmtDate(a.published_date)}</span> · {a.ai_summary}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <select value={a.community_category} onChange={(e) => patchArticle(a.id, { community_category: e.target.value })} className="text-[12px] border border-stone-200 rounded px-1.5 py-1" data-testid={`cnews-cat-${a.id}`}>
                      {Object.entries(CAT_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                    <button onClick={() => patchArticle(a.id, { is_pinned: !a.is_pinned })} title="고정" className={`p-1.5 rounded hover:bg-stone-100 ${a.is_pinned ? "text-amber-600" : "text-stone-400"}`} data-testid={`cnews-pin-${a.id}`}><Pin className="w-4 h-4" /></button>
                    <button onClick={() => patchArticle(a.id, { hidden: !a.hidden })} title="숨기기" className="p-1.5 rounded hover:bg-stone-100 text-stone-400" data-testid={`cnews-hide-${a.id}`}>{a.hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
                    <button onClick={() => delArticle(a.id)} title="삭제" className="p-1.5 rounded hover:bg-red-50 text-red-400" data-testid={`cnews-del-${a.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
